/**
 * Access to the football knowledge base.
 *
 * Concepts are curated, versioned with the app, and identical for every
 * user, so the MVP ships them in the bundle rather than reading them from
 * Firestore on every screen. That keeps the Learn tab instant and costs
 * zero Firestore reads (PRD §40).
 *
 * The Firestore collection `footballConcepts/{conceptId}` is still
 * reserved and locked read-only in the security rules, so this service can
 * be pointed at it later without touching a single component.
 */

import {
  CONCEPTS,
  CONCEPTS_BY_ID,
  DIFFICULTY_LABELS,
} from "../../data/concepts/concepts";
import { conceptIdForEvent } from "../../data/concepts/eventConceptMap";

export function listConcepts() {
  return CONCEPTS;
}

export function getConcept(conceptId) {
  return CONCEPTS_BY_ID[conceptId] ?? null;
}

export function getConcepts(conceptIds = []) {
  return conceptIds.map(getConcept).filter(Boolean);
}

export function getAdjacentConcepts(conceptId) {
  const index = CONCEPTS.findIndex((concept) => concept.conceptId === conceptId);
  const total = CONCEPTS.length;

  if (index < 0) {
    return { previous: null, next: null, index: -1, total };
  }

  return {
    previous: index > 0 ? CONCEPTS[index - 1] : null,
    next: index < total - 1 ? CONCEPTS[index + 1] : null,
    index,
    total,
  };
}

export function getConceptForEvent(eventType) {
  const conceptId = conceptIdForEvent(eventType);
  return conceptId ? getConcept(conceptId) : null;
}

/** Concepts grouped by difficulty, ascending, for the Learn tab. */
export function listConceptsByDifficulty() {
  const groups = new Map();

  CONCEPTS.forEach((concept) => {
    if (!groups.has(concept.difficulty)) groups.set(concept.difficulty, []);
    groups.get(concept.difficulty).push(concept);
  });

  return [...groups.entries()]
    .sort(([a], [b]) => a - b)
    .map(([difficulty, concepts]) => ({
      difficulty,
      label: DIFFICULTY_LABELS[difficulty] ?? `Level ${difficulty}`,
      concepts,
    }));
}

/** Concepts grouped by their display category, in first-appearance order. */
export function listConceptsByCategory() {
  const groups = new Map();

  CONCEPTS.forEach((concept) => {
    if (!groups.has(concept.category)) groups.set(concept.category, []);
    groups.get(concept.category).push(concept);
  });

  return [...groups.entries()].map(([category, concepts]) => ({
    category,
    concepts,
  }));
}

/** Simple title/explanation substring search. */
export function searchConcepts(query) {
  const needle = String(query ?? "").trim().toLowerCase();
  if (!needle) return CONCEPTS;

  return CONCEPTS.filter((concept) =>
    [concept.title, concept.shortExplanation, concept.category, concept.conceptId]
      .join(" ")
      .toLowerCase()
      .includes(needle)
  );
}

/**
 * The next concept a learner hasn't seen, easiest first.
 * Powers the "Continue Learning" card on Home (PRD §11).
 */
export function getNextConcept(learnedIds = []) {
  const learned = new Set(learnedIds);
  return (
    [...CONCEPTS]
      .sort((a, b) => a.difficulty - b.difficulty)
      .find((concept) => !learned.has(concept.conceptId)) ?? null
  );
}

export const TOTAL_CONCEPTS = CONCEPTS.length;
